// eslint-disable-next-line jira/restricted/react-component-props
import React, { useEffect, type ComponentProps, type ComponentType } from 'react';
import flow from 'lodash/flow';
import noop from 'lodash/noop';
import { useAnalyticsEvents, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Inline } from '@atlaskit/primitives/compiled';
import AddIcon from '@atlaskit/icon/core/add';
import { useIsDismissed } from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import PlatformColumnCreate from '@atlassian/jira-platform-board-kit/src/ui/column/column-create/index.tsx';
import {
	fireUIAnalytics,
	AnalyticsEventToProps,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { getIsSoftwareThemingEnabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { ThemedIconButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button-new/ThemedButtonNew.tsx';
import { connect } from '@atlassian/jira-react-redux/src/index.tsx';
import { BoardCreateColumnButtonWrapperNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/board-create-column/async.tsx';
import { ConditionalNudgeWrapper } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/conditional-nudge-wrapper/index.tsx';
import { BoardCreateColumnKanbanTourNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/kanban-onboarding-tour/board-create-column/async.tsx';
import type { RenderCustomButton } from '@atlassian/jira-platform-board-kit/src/common/types.tsx';
import { columnCreateRequest } from '../../../../state/actions/column/create/index.tsx';
import { useBoardSelector } from '../../../../state/index.tsx';
import type { State } from '../../../../state/reducers/types.tsx';
import { makeValidateColumnSelector } from '../../../../state/selectors/column/column-selectors.tsx';
import { getIsCMPBoard } from '../../../../state/selectors/software/software-selectors.tsx';
import { isSprintsEnabled } from '../../../../state/selectors/sprint/sprint-selectors.tsx';
import { isAnyColumnUpdating } from '../../../../state/selectors/work/work-selectors.tsx';
import type { Dispatch } from '../../../../state/types.tsx';
import { useHasGroupingStyle } from '../../../../common/hooks/use-has-grouping-style/useHasGroupingStyle.tsx';

type OwnProps = {
	isCMPBoard: boolean;
};

type Props = {
	isKanbanBoard: boolean;
} & ComponentProps<typeof PlatformColumnCreate>;

const connector = connect(
	(state: State) => ({
		// analytics
		externalId: 'software.board.column.add.title',
		onEditStart: noop,
		onEditCancel: noop,
		validate: makeValidateColumnSelector(state),
		isDisabled: isAnyColumnUpdating(state),
		isKanbanBoard: !isSprintsEnabled(state),
	}),
	(dispatch: Dispatch, ownProps: OwnProps) => ({
		onSubmit: (temporaryColumnId: number, name: string, analyticsEvent: UIAnalyticsEvent) => {
			const action = columnCreateRequest(
				temporaryColumnId,
				name,
				analyticsEvent,
				ownProps.isCMPBoard,
			);
			dispatch(action);
		},
		onEditStart: (analyticsEvent: UIAnalyticsEvent) => fireUIAnalytics(analyticsEvent),
		onEditCancel: (columnId: number, type: string, analyticsEvent: UIAnalyticsEvent) =>
			fireUIAnalytics(analyticsEvent, { type, columnId }),
	}),
);

export const renderCustomButton: RenderCustomButton = ({ ariaLabel, ...props }) => (
	<ThemedIconButton icon={AddIcon} {...props} label={ariaLabel} />
);

// Wraps ColumnCreate with a NudgeToolTip
export const withQuickstartNudge =
	<P extends Props>(WrappedComponent: ComponentType<P>): ComponentType<P> =>
	(ownProps: P) => {
		const [isOnboardingDismissed] = useIsDismissed();
		const AsyncNudgeComponent = ownProps.isKanbanBoard
			? BoardCreateColumnKanbanTourNudgeAsync
			: BoardCreateColumnButtonWrapperNudgeAsync;
		const hasGrouping = useHasGroupingStyle();

		return (
			<ConditionalNudgeWrapper
				conditionsToApplyWrapper={!isOnboardingDismissed}
				Wrapper={AsyncNudgeComponent}
			>
				<Inline grow="hug">
					<WrappedComponent
						{...ownProps}
						{...(getIsSoftwareThemingEnabled() && {
							renderCustomButton,
							isMatrixLayout: hasGrouping,
						})}
					/>
				</Inline>
			</ConditionalNudgeWrapper>
		);
	};

const ColumnCreate = flow(
	withQuickstartNudge,
	AnalyticsEventToProps('columnCreate', {
		onEditStart: 'editStarted',
		onEditCancel: 'editCanceled',
	}),
	connector,
)(PlatformColumnCreate);

const ColumnCreateWrapper = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isCMPBoard = useBoardSelector(getIsCMPBoard);
	useEffect(() => {
		if (isCMPBoard) {
			const analyticsEvent = createAnalyticsEvent({});
			fireOperationalAnalytics(analyticsEvent, 'columnCreateButton mounted');
		}
	}, [isCMPBoard, createAnalyticsEvent]);

	return <ColumnCreate isCMPBoard={isCMPBoard} />;
};

export default ColumnCreateWrapper;
